import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, ShieldAlert, Copy, Check, Gavel, AlertTriangle, Lightbulb,
  Paperclip, Search, ChevronDown, Info, History, Trash2, Clock, Users, BarChart3, Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('kylo_token')}` });

const getDeviceId = () => {
  let id = localStorage.getItem('poettik_device_id');
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || `d_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('poettik_device_id', id);
  }
  return id;
};

const CHECK_STEPS = [
  'Searching previous violations…',
  'Matching your violation type…',
  'Comparing appeal outcomes…',
  'Tailoring your result…',
];

const REC_STYLE = {
  appeal_misclassification: { label: 'Appeal: misclassification', cls: 'bg-[#DCFCE7] text-[#15803D] border-[#BBF7D0]' },
  appeal_mitigation: { label: 'Appeal: mitigation', cls: 'bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]' },
  do_not_appeal: { label: 'Do not appeal', cls: 'bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]' },
};
const BAND_CLR = { High: '#15803D', Medium: '#B45309', Low: '#B91C1C' };
const STATUS = {
  pending: { label: 'Pending', cls: 'bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]' },
  approved: { label: 'Approved', cls: 'bg-[#DCFCE7] text-[#15803D] border-[#BBF7D0]' },
  denied: { label: 'Denied', cls: 'bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]' },
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; } };

const Disclaimer = ({ text, compact }) => (
  <div
    className={`flex gap-2.5 rounded-[12px] border border-[#EADFce] bg-[#FBF4E6] text-[#8A6D3B] ${compact ? 'p-3' : 'p-4'}`}
    data-testid="violation-disclaimer"
  >
    <Info className="w-4 h-4 shrink-0 mt-0.5" />
    <p className="text-xs leading-snug">{text}</p>
  </div>
);

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-sm text-[#1F1B16]">{label}</Label>
    {children}
    {hint && <p className="text-xs text-[#9A9188]">{hint}</p>}
  </div>
);

const inputCls = 'bg-[#FFFDF9] border-[#E6DED1] focus-visible:ring-[#9D174D]/30';

/* "Checking previous violation results" animation — always plays ~3-4s. */
const CheckingAnimation = ({ step }) => (
  <div className="rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] p-6" data-testid="violation-checking">
    <div className="flex items-center gap-3 mb-4">
      <span className="w-9 h-9 rounded-full bg-[#FBE4EE] text-[#9D174D] flex items-center justify-center shrink-0">
        <Loader2 className="w-5 h-5 animate-spin" />
      </span>
      <p className="font-display text-base text-[#1F1B16]">Checking previous violation results…</p>
    </div>
    <ul className="space-y-2.5">
      {CHECK_STEPS.map((s, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <li key={i} className="flex items-center gap-2.5 text-sm" data-testid={`violation-check-step-${i}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 border transition-colors ${done ? 'bg-[#15803D] border-[#15803D] text-white' : active ? 'border-[#9D174D] text-[#9D174D]' : 'border-[#E6DED1] text-transparent'}`}>
              {done ? <Check className="w-3 h-3" /> : active ? <Loader2 className="w-3 h-3 animate-spin" /> : <span className="w-1.5 h-1.5 rounded-full bg-[#E6DED1]" />}
            </span>
            <span className={done ? 'text-[#6F675D]' : active ? 'text-[#1F1B16] font-medium' : 'text-[#B8AFA2]'}>{s}</span>
          </li>
        );
      })}
    </ul>
  </div>
);

/* Small trustworthy community line inside a result. */
const CommunitySignal = ({ community }) => {
  if (!community || !community.shown) return null;
  return (
    <div className="flex gap-2.5 rounded-[12px] border border-[#D7E8DC] bg-[#F1F8F3] p-3.5" data-testid="violation-community-signal">
      <Users className="w-4 h-4 shrink-0 mt-0.5 text-[#15803D]" />
      <p className="text-sm text-[#2F4A38] leading-snug">
        <span className="font-semibold">Community:</span> {community.approved} of {community.recorded} similar appeals were reported successful ({community.success_rate}%).
        <span className="block text-xs text-[#6F8A73] mt-0.5">Based on self-reported outcomes from other Poettik creators. Your result reflects this.</span>
      </p>
    </div>
  );
};

/* Viewable aggregate stats section — aggregate numbers only. */
const CommunityStats = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/violation/community-stats`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || !data.stats || data.stats.length === 0) return null;
  return (
    <div className="rounded-[16px] border border-[#E6DED1] bg-[#FFFDF9] p-5" data-testid="violation-community-stats">
      <h3 className="flex items-center gap-2 font-display text-lg text-[#1F1B16] mb-1">
        <BarChart3 className="w-5 h-5 text-[#9D174D]" /> Community appeal outcomes
      </h3>
      <p className="text-xs text-[#9A9188] mb-4">How appeals have actually gone for other creators, by violation type. Self-reported outcomes, aggregate numbers only.</p>
      <div className="space-y-3">
        {data.stats.map((s) => (
          <div key={s.violation_type} data-testid={`community-stat-${s.violation_type}`}>
            <div className="flex items-center justify-between gap-3 mb-1">
              <span className="text-sm text-[#1F1B16] truncate">{s.label}</span>
              <span className="text-sm font-semibold shrink-0" style={{ color: s.success_rate >= 50 ? '#15803D' : s.success_rate >= 25 ? '#B45309' : '#B91C1C' }}>
                {s.success_rate}%
              </span>
            </div>
            <div className="h-2 rounded-full bg-[#F0E8DC] overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${s.success_rate}%`, backgroundColor: s.success_rate >= 50 ? '#15803D' : s.success_rate >= 25 ? '#B45309' : '#B91C1C' }} />
            </div>
            <p className="text-xs text-[#9A9188] mt-1">{s.approved} of {s.recorded} appeals reported successful</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ViolationWizard({ mode = 'app' }) {
  const isPublic = mode === 'public';
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    violation_type: '', other_type: '', context: '', product: '', dispute_reason: '',
    policy_text: '', violation_date: '',
    enforcement: [], transcript: '', on_screen_text: '', product_shown: '', listing_claims: '',
    existing_evidence: '', repeat_count: '',
  });
  const [allowLong, setAllowLong] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkStep, setCheckStep] = useState(0);
  const [result, setResult] = useState(null);
  const [wall, setWall] = useState(null);
  const [appealDraft, setAppealDraft] = useState('');
  const [copied, setCopied] = useState(false);
  const [appeals, setAppeals] = useState([]);
  const [usage, setUsage] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const loadAppeals = async () => {
    try {
      const r = await axios.get(`${API}/api/violation/appeals`, { headers: authHeaders() });
      setAppeals(r.data.appeals || []);
    } catch { /* ignore */ }
  };

  const loadUsage = async () => {
    try {
      const r = await axios.get(`${API}/api/violation/usage`, { headers: authHeaders() });
      setUsage(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    axios.get(`${API}/api/violation/config`).then((r) => setConfig(r.data)).catch(() => {});
    if (!isPublic) { loadAppeals(); loadUsage(); }
  }, [isPublic]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleEnf = (val) => setForm((f) => ({
    ...f,
    enforcement: f.enforcement.includes(val) ? f.enforcement.filter((e) => e !== val) : [...f.enforcement, val],
  }));

  const disclaimer = config?.disclaimer || 'This is AI-generated guidance only. Not guaranteed and not legal advice. Use at your own discretion.';

  const generate = async () => {
    if (!form.violation_type) { toast.error('Please choose a violation type.'); return; }
    if (!form.context.trim()) { toast.error('Please add a bit of context about the problem.'); return; }
    setLoading(true);
    setResult(null);
    setWall(null);
    setCheckStep(0);
    const stepTimer = setInterval(() => setCheckStep((s) => Math.min(s + 1, CHECK_STEPS.length - 1)), 900);
    const minDelay = new Promise((r) => setTimeout(r, 3600));
    try {
      const payload = {
        violation_type: form.violation_type,
        other_type: form.other_type,
        context: form.context,
        product: form.product,
        dispute_reason: form.dispute_reason,
        policy_text: form.policy_text,
        violation_date: form.violation_date,
        enforcement: form.enforcement,
        transcript: form.transcript,
        on_screen_text: form.on_screen_text,
        product_shown: form.product_shown,
        listing_claims: form.listing_claims,
        existing_evidence: form.existing_evidence,
        repeat_count: form.repeat_count ? parseInt(form.repeat_count, 10) : 0,
        allow_long: allowLong,
      };
      let reqP;
      if (isPublic) {
        reqP = axios.post(`${API}/api/violation/appeal-public`, { ...payload, device_id: getDeviceId() });
      } else {
        reqP = axios.post(`${API}/api/violation/appeal`, payload, { headers: authHeaders() });
      }
      const [res] = await Promise.all([reqP, minDelay]);
      if (isPublic && res.data?.success === false) {
        setWall(res.data.message || 'You have used your free assessment. Create a free account to run more.');
        setTimeout(() => document.getElementById('violation-wall')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
        return;
      }
      setResult(res.data);
      setAppealDraft(res.data.appeal_text || '');
      if (res.data?.usage) setUsage(res.data.usage);
      if (!isPublic) loadAppeals();
      setTimeout(() => document.getElementById('violation-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not assess this violation. Please try again.');
    } finally {
      clearInterval(stepTimer);
      setLoading(false);
    }
  };

  const copyAppeal = async () => {
    try {
      await navigator.clipboard.writeText(appealDraft);
      setCopied(true);
      toast.success('Appeal copied');
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error('Copy failed. Select and copy manually.'); }
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text || ''); toast.success('Appeal copied'); }
    catch { toast.error('Copy failed.'); }
  };

  const setStatus = async (id, status) => {
    setAppeals((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    try {
      await axios.patch(`${API}/api/violation/appeals/${id}`, { status }, { headers: authHeaders() });
    } catch { toast.error('Could not update status.'); loadAppeals(); }
  };

  const deleteAppeal = async (id) => {
    try {
      await axios.delete(`${API}/api/violation/appeals/${id}`, { headers: authHeaders() });
      setAppeals((prev) => prev.filter((a) => a.id !== id));
      toast.success('Appeal removed');
    } catch { toast.error('Could not delete.'); }
  };

  const rec = result && (REC_STYLE[result.recommendation] || REC_STYLE.do_not_appeal);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="violation-wizard">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-[12px] bg-[#FBE4EE] text-[#9D174D] flex items-center justify-center shrink-0">
          <Gavel className="w-5 h-5" strokeWidth={1.8} />
        </span>
        <div>
          <h1 className="font-display text-xl sm:text-2xl text-[#1F1B16] leading-tight">Appeal a TikTok Shop violation</h1>
          <p className="text-sm text-[#6F675D] mt-0.5">
            {isPublic
              ? 'Get an honest assessment and a submit-ready appeal. Your first one is free, no account needed.'
              : 'Get an honest assessment and a submit-ready appeal. You only get one appeal per violation, so make it count.'}
          </p>
        </div>
      </div>

      <Disclaimer text={disclaimer} />

      {/* Form */}
      <div className="rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] p-5 space-y-5">
        <Field label="Violation type *">
          <select
            value={form.violation_type}
            onChange={(e) => set('violation_type', e.target.value)}
            className={`w-full h-10 rounded-md border px-3 text-sm ${inputCls} text-[#1F1B16]`}
            data-testid="violation-type-select"
          >
            <option value="">Select the violation on your notice…</option>
            {(config?.violation_types || []).map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </Field>

        {form.violation_type === 'other' && (
          <Field label="Describe the violation type">
            <Input className={inputCls} value={form.other_type} onChange={(e) => set('other_type', e.target.value)} placeholder="e.g. Spam / low-quality content" data-testid="violation-other-type" />
          </Field>
        )}

        <Field label="Context *" hint="Please provide a bit of context as to what the problem was.">
          <Textarea className={`${inputCls} min-h-[110px]`} value={form.context} onChange={(e) => set('context', e.target.value)} placeholder="Tell us what your video showed and what TikTok flagged…" data-testid="violation-context" />
        </Field>

        <Field label="Product" hint="What product was in the video or listing?">
          <Input className={inputCls} value={form.product} onChange={(e) => set('product', e.target.value)} placeholder="e.g. Vitamin C serum" data-testid="violation-product" />
        </Field>

        <Field label="Why you think this violation is wrong" hint="Optional. Tell us why you believe it was flagged incorrectly.">
          <Textarea className={`${inputCls} min-h-[80px]`} value={form.dispute_reason} onChange={(e) => set('dispute_reason', e.target.value)} placeholder="e.g. I never claimed a guaranteed result, I shared my own experience." data-testid="violation-dispute-reason" />
        </Field>

        {/* Optional detail */}
        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          className="flex items-center gap-1.5 text-sm font-medium text-[#9D174D]"
          data-testid="violation-toggle-detail"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${showMore ? 'rotate-180' : ''}`} />
          {showMore ? 'Hide extra detail' : 'Add more detail (optional, improves the result)'}
        </button>

        {showMore && (
          <div className="space-y-5 pt-1" data-testid="violation-detail-fields">
            <Field label="Policy text quoted on the notice">
              <Textarea className={`${inputCls} min-h-[70px]`} value={form.policy_text} onChange={(e) => set('policy_text', e.target.value)} placeholder="Paste the exact policy wording TikTok showed you." />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Violation date">
                <Input type="date" className={inputCls} value={form.violation_date} onChange={(e) => set('violation_date', e.target.value)} />
              </Field>
              <Field label="Times this happened (last 90 days)">
                <Input type="number" min="0" className={inputCls} value={form.repeat_count} onChange={(e) => set('repeat_count', e.target.value)} placeholder="0" data-testid="violation-repeat-count" />
              </Field>
            </div>
            <Field label="Enforcement applied">
              <div className="flex flex-wrap gap-2">
                {(config?.enforcement_options || []).map((o) => {
                  const on = form.enforcement.includes(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => toggleEnf(o.value)}
                      className={`px-3 h-9 rounded-full border text-xs font-medium transition-colors ${on ? 'bg-[#1F1B16] text-[#FFFDF9] border-[#1F1B16]' : 'bg-[#FFFDF9] text-[#5E574E] border-[#E6DED1]'}`}
                      data-testid={`violation-enforcement-${o.value}`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Video transcript">
              <Textarea className={`${inputCls} min-h-[70px]`} value={form.transcript} onChange={(e) => set('transcript', e.target.value)} placeholder="What you said in the video." />
            </Field>
            <Field label="On-screen text / captions">
              <Textarea className={`${inputCls} min-h-[60px]`} value={form.on_screen_text} onChange={(e) => set('on_screen_text', e.target.value)} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Product shown">
                <Input className={inputCls} value={form.product_shown} onChange={(e) => set('product_shown', e.target.value)} />
              </Field>
              <Field label="Product listing claims">
                <Input className={inputCls} value={form.listing_claims} onChange={(e) => set('listing_claims', e.target.value)} />
              </Field>
            </div>
            <Field label="Evidence you already have">
              <Textarea className={`${inputCls} min-h-[60px]`} value={form.existing_evidence} onChange={(e) => set('existing_evidence', e.target.value)} placeholder="e.g. a screenshot showing the product on screen at 0:04." />
            </Field>
          </div>
        )}

        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="violation-allow-long-wrap">
          <Checkbox checked={allowLong} onCheckedChange={(v) => setAllowLong(!!v)} data-testid="violation-allow-long" className="mt-0.5" />
          <span className="text-sm text-[#5E574E]">My notice allows a longer appeal: let the appeal exceed {config?.short_cap || 500} characters (up to {config?.long_cap || 1000}).</span>
        </label>

        <Button onClick={generate} disabled={loading} className="w-full h-11" data-testid="violation-generate-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</> : <><ShieldAlert className="w-4 h-4 mr-2" /> Assess & draft appeal</>}
        </Button>
        <p className="text-xs text-center text-[#9A9188]" data-testid="violation-usage-note">
          {isPublic
            ? 'Your first assessment is free. No account needed. No card, no catch.'
            : usage
              ? `${usage.remaining} of ${usage.limit} violation appeals left this month on your ${usage.plan.charAt(0).toUpperCase() + usage.plan.slice(1)} plan.`
              : 'Your plan includes a set number of violation appeals each month.'}
        </p>
      </div>

      {/* Checking animation */}
      {loading && <CheckingAnimation step={checkStep} />}

      {/* Sign-up wall (public, after free use) */}
      {wall && !loading && (
        <div id="violation-wall" className="rounded-[16px] border border-[#F3C9DD] bg-[#FDEFF5] p-6 text-center" data-testid="violation-wall">
          <span className="w-11 h-11 rounded-full bg-[#FBE4EE] text-[#9D174D] flex items-center justify-center mx-auto mb-3">
            <Lock className="w-5 h-5" />
          </span>
          <h3 className="font-display text-lg text-[#1F1B16] mb-1">That's your free assessment used</h3>
          <p className="text-sm text-[#6F675D] max-w-md mx-auto mb-4">{wall}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild className="h-11 px-6" data-testid="violation-wall-register">
              <Link to="/register">Create a free account</Link>
            </Button>
            <Button asChild variant="outline" className="h-11 px-6" data-testid="violation-wall-login">
              <Link to="/login">Sign in</Link>
            </Button>
          </div>
        </div>
      )}

      {/* Result */}
      {result && !loading && (
        <div id="violation-result" className="space-y-4" data-testid="violation-result">
          {result.repeat_warning && (
            <div className="flex gap-2.5 rounded-[12px] border border-[#FECACA] bg-[#FEE2E2] p-4 text-[#B91C1C]" data-testid="violation-repeat-warning">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <p className="text-sm leading-snug">{result.repeat_warning}</p>
            </div>
          )}

          <div className="rounded-[16px] border border-[#E6DED1] bg-[#FFFDF9] p-5 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${rec.cls}`} data-testid="violation-recommendation">
                {result.recommendation_label || rec.label}
              </span>
              <div className="flex items-center gap-2" data-testid="violation-probability">
                <span className="text-2xl font-display" style={{ color: BAND_CLR[result.success_band] || '#6F675D' }}>{result.success_probability}%</span>
                <span className="text-xs text-[#6F675D] leading-tight">est. chance<br />of success</span>
                <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium border border-[#E6DED1] text-[#6F675D]">{result.success_band}</span>
              </div>
            </div>

            <CommunitySignal community={result.community} />

            {result.reasoning && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-[#8A8178] mb-1">Why</p>
                <p className="text-sm text-[#3F3A33] leading-relaxed" data-testid="violation-reasoning">{result.reasoning}</p>
                {result.policy_section && <p className="text-xs text-[#9A9188] mt-1.5">Policy: {result.policy_section}</p>}
              </div>
            )}

            {appealDraft ? (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[11px] uppercase tracking-wider text-[#8A8178]">Your appeal (editable)</p>
                  <span className="text-xs text-[#9A9188]">{appealDraft.length}/{result.char_cap}</span>
                </div>
                <Textarea
                  value={appealDraft}
                  onChange={(e) => setAppealDraft(e.target.value.slice(0, result.char_cap))}
                  maxLength={result.char_cap}
                  className="bg-[#FBF7F0] border-[#E6DED1] min-h-[150px] text-sm"
                  data-testid="violation-appeal-text"
                />
                <Button onClick={copyAppeal} variant="outline" size="sm" className="mt-2" data-testid="violation-copy-appeal">
                  {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy appeal</>}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-[#6F675D] italic">No appeal drafted. The recommendation is not to appeal this one.</p>
            )}
          </div>

          {(result.evidence_to_attach?.length > 0 || result.evidence_to_gather?.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {result.evidence_to_attach?.length > 0 && (
                <div className="rounded-[16px] border border-[#E6DED1] bg-[#FFFDF9] p-4" data-testid="violation-evidence-attach">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-[#1F1B16] mb-2"><Paperclip className="w-4 h-4 text-[#9D174D]" /> Evidence to attach</p>
                  <ul className="space-y-1.5">{result.evidence_to_attach.map((e, i) => <li key={i} className="text-sm text-[#3F3A33] flex gap-2"><span className="text-[#9D174D]">•</span>{e}</li>)}</ul>
                </div>
              )}
              {result.evidence_to_gather?.length > 0 && (
                <div className="rounded-[16px] border border-[#E6DED1] bg-[#FFFDF9] p-4" data-testid="violation-evidence-gather">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-[#1F1B16] mb-2"><Search className="w-4 h-4 text-[#9D174D]" /> Evidence to gather</p>
                  <ul className="space-y-1.5">{result.evidence_to_gather.map((e, i) => <li key={i} className="text-sm text-[#3F3A33] flex gap-2"><span className="text-[#9D174D]">•</span>{e}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          {result.prevention_tip && (
            <div className="flex gap-2.5 rounded-[12px] border border-[#E6DED1] bg-[#FBF7F0] p-4" data-testid="violation-prevention">
              <Lightbulb className="w-4 h-4 shrink-0 mt-0.5 text-[#9D174D]" />
              <p className="text-sm text-[#3F3A33]"><span className="font-medium">Next time:</span> {result.prevention_tip}</p>
            </div>
          )}

          {isPublic && (
            <div className="rounded-[14px] border border-[#EFE1D1] bg-[#FBF7F0] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="violation-public-cta">
              <p className="text-sm text-[#3F3A33] flex-1">Want to run more appeals and keep a saved history of your outcomes? It's free to start.</p>
              <Button asChild size="sm" className="shrink-0"><Link to="/register">Create a free account</Link></Button>
            </div>
          )}

          <Disclaimer text={disclaimer} compact />
        </div>
      )}

      {/* Community outcomes stats */}
      {!loading && <CommunityStats />}

      {/* Appeal history (app only) */}
      {!isPublic && appeals.length > 0 && (
        <div className="pt-2" data-testid="appeal-history">
          <h3 className="flex items-center gap-2 font-display text-lg text-[#1F1B16] mb-3">
            <History className="w-5 h-5 text-[#9D174D]" /> Appeal history
          </h3>
          <div className="space-y-3">
            {appeals.map((a) => {
              const arec = REC_STYLE[a.recommendation] || REC_STYLE.do_not_appeal;
              const st = STATUS[a.status] || STATUS.pending;
              const open = expandedId === a.id;
              return (
                <div key={a.id} className="rounded-[14px] border border-[#E6DED1] bg-[#FFFDF9] overflow-hidden" data-testid={`appeal-item-${a.id}`}>
                  <div className="flex items-start gap-3 p-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-[#1F1B16] truncate">{a.violation_label}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${arec.cls}`}>{a.recommendation_label || arec.label}</span>
                        <span className="text-xs text-[#9A9188] inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(a.created_at)}</span>
                        <span className="text-xs text-[#6F675D]">· {a.success_probability}% {a.success_band}</span>
                      </div>
                      <p className="text-xs text-[#6F675D] mt-1 line-clamp-2">{a.context}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <select
                        value={a.status}
                        onChange={(e) => setStatus(a.id, e.target.value)}
                        className={`h-8 rounded-full border text-xs font-medium px-2 ${st.cls}`}
                        data-testid={`appeal-status-${a.id}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="denied">Denied</option>
                      </select>
                      <button onClick={() => deleteAppeal(a.id)} className="w-8 h-8 rounded-full flex items-center justify-center text-[#B91C1C] hover:bg-[#FEE2E2]" title="Delete" data-testid={`appeal-delete-${a.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => setExpandedId(open ? null : a.id)}
                    className="w-full flex items-center justify-center gap-1 py-1.5 text-xs font-medium text-[#9D174D] border-t border-[#EFE8DD] hover:bg-[#FBF7F0]"
                    data-testid={`appeal-expand-${a.id}`}
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                    {open ? 'Hide details' : 'View appeal & evidence'}
                  </button>
                  {open && (
                    <div className="p-4 pt-3 space-y-3 border-t border-[#EFE8DD] bg-[#FBF7F0]/50">
                      {a.reasoning && <p className="text-sm text-[#3F3A33]">{a.reasoning}</p>}
                      {a.appeal_text ? (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-[11px] uppercase tracking-wider text-[#8A8178]">Appeal</p>
                            <button onClick={() => copyText(a.appeal_text)} className="text-xs text-[#9D174D] inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copy</button>
                          </div>
                          <p className="text-sm text-[#1F1B16] whitespace-pre-wrap bg-[#FFFDF9] border border-[#E6DED1] rounded-lg p-3">{a.appeal_text}</p>
                        </div>
                      ) : <p className="text-sm text-[#6F675D] italic">No appeal was drafted for this one.</p>}
                      {a.evidence_to_attach?.length > 0 && (
                        <div>
                          <p className="text-[11px] uppercase tracking-wider text-[#8A8178] mb-1">Evidence to attach</p>
                          <ul className="space-y-1">{a.evidence_to_attach.map((e, i) => <li key={i} className="text-sm text-[#3F3A33] flex gap-2"><span className="text-[#9D174D]">•</span>{e}</li>)}</ul>
                        </div>
                      )}
                      {a.evidence_to_gather?.length > 0 && (
                        <div>
                          <p className="text-[11px] uppercase tracking-wider text-[#8A8178] mb-1">Evidence to gather</p>
                          <ul className="space-y-1">{a.evidence_to_gather.map((e, i) => <li key={i} className="text-sm text-[#3F3A33] flex gap-2"><span className="text-[#9D174D]">•</span>{e}</li>)}</ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
